/**
 * Hybrid Video Exporter
 *
 * Combine WebCodecs (pour intro/outro avec transparence) et MediaRecorder (pour le contenu principal, plus rapide)
 *
 * STRATÉGIE:
 * 1. Détection des segments: intro (clipRole='intro'), contenu principal (pas de clipRole), outro (clipRole='outro')
 * 2. Export par segment:
 *    - Intro/Outro: WebCodecsExporter (VP9 avec alpha channel pour transparence)
 *    - Contenu principal: MediaRecorder (plus rapide, pas de transparence nécessaire)
 * 3. Concatenation: Fusion des segments vidéo + audio
 *
 * LIMITATIONS ACTUELLES:
 * - La concatenation de segments WebM est fragile et peut ne pas fonctionner sur tous les navigateurs
 * - MediaRecorder avec OffscreenCanvas.captureStream() peut ne pas être supporté partout
 * - L'audio séparé n'est pas encore multiplexé correctement
 *
 * FALLBACK:
 * - Si pas d'intro/outro: utilise WebCodecsExporter directement (garanti de fonctionner)
 * - En cas d'erreur: le système retombe sur WebCodecsExporter complet
 *
 * TODO FUTUR:
 * - Implémenter une vraie concatenation avec mp4box.js ou ffmpeg.wasm
 * - Améliorer le multiplexage audio/vidéo
 * - Ajouter support pour transitions entre segments
 */

import { Clip, Track, SubtitleStyle } from './types';
import { WebCodecsExporter, ExportConfig, ProgressCallback, ExportProgress } from './webcodecs-exporter';
import { FrameRenderer } from './frame-renderer';
import { AudioMixer } from './audio-mixer';

export type HybridExportConfig = ExportConfig;

interface TimelineSegment {
  type: 'intro' | 'main' | 'outro';
  clips: Clip[];
  start: number;
  end: number;
  hasTransparency: boolean;
}

export class HybridExporter {
  private config: HybridExportConfig;
  private progressCallback: ProgressCallback;
  private webCodecsExporter: WebCodecsExporter;
  private canvasWidth: number;
  private canvasHeight: number;
  private aborted = false;

  constructor(config: HybridExportConfig, progressCallback: ProgressCallback) {
    this.config = config;
    this.progressCallback = progressCallback;

    // Calcul des dimensions basé sur l'aspect ratio
    const aspectRatios = {
      '16:9': { width: 1920, height: 1080 },
      '1:1': { width: 1080, height: 1080 },
      '9:16': { width: 1080, height: 1920 },
    };
    const dimensions = aspectRatios[config.aspectRatio];
    this.canvasWidth = dimensions.width;
    this.canvasHeight = dimensions.height;

    // Initialiser le WebCodecsExporter pour les segments avec transparence
    this.webCodecsExporter = new WebCodecsExporter(config, progressCallback);
  }

  /**
   * Abort current export
   */
  abort(): void {
    this.aborted = true;
    this.webCodecsExporter.abort();
  }

  /**
   * Vérifie si OffscreenCanvas.captureStream est supporté
   */
  private isMediaRecorderSupported(): boolean {
    try {
      // Vérifier si OffscreenCanvas existe et a captureStream
      if (typeof OffscreenCanvas === 'undefined') {
        return false;
      }

      const testCanvas = new OffscreenCanvas(1, 1);
      return typeof (testCanvas as any).captureStream === 'function';
    } catch (e) {
      return false;
    }
  }

  /**
   * Export principal avec logique hybride
   */
  async export(
    clips: Clip[],
    tracks: Track[],
    activeSubtitleLanguage: string,
    subtitleStyle: SubtitleStyle
  ): Promise<Blob> {
    this.updateProgress({ phase: 'preparing', progress: 0, message: 'Analyse du timeline...' });

    // 1. Vérifier si MediaRecorder avec OffscreenCanvas est supporté
    const canUseMediaRecorder = this.isMediaRecorderSupported();

    if (!canUseMediaRecorder) {
      // Fallback vers WebCodecs complet si MediaRecorder n'est pas supporté
      this.updateProgress({
        phase: 'preparing',
        progress: 5,
        message: 'MediaRecorder non supporté, utilisation de WebCodecs...'
      });
      return this.webCodecsExporter.export(clips, tracks, activeSubtitleLanguage, subtitleStyle);
    }

    // 2. Segmentation du timeline
    const segments = this.segmentTimeline(clips);

    // 3. Vérifier si on a besoin du mode hybride
    const hasIntro = segments.some(s => s.type === 'intro');
    const hasOutro = segments.some(s => s.type === 'outro');
    const needsHybrid = hasIntro || hasOutro;

    if (!needsHybrid) {
      // Pas d'intro/outro -> utiliser 100% WebCodecs (pour la cohérence)
      this.updateProgress({ phase: 'preparing', progress: 5, message: 'Aucune intro/outro détectée, export standard...' });
      return this.webCodecsExporter.export(clips, tracks, activeSubtitleLanguage, subtitleStyle);
    }

    // 4. Export hybride
    this.updateProgress({ phase: 'preparing', progress: 10, message: 'Mode hybride activé : intro/outro avec transparence...' });

    const videoBlobs: Blob[] = [];
    let audioBlob: Blob | null = null;
    let currentProgress = 10;
    const progressPerSegment = 70 / segments.length;

    // 5. Exporter chaque segment
    for (const segment of segments) {
      if (this.aborted) {
        throw new Error('Export annulé par l\'utilisateur');
      }

      const segmentLabel = segment.type === 'intro' ? 'Intro' : segment.type === 'outro' ? 'Outro' : 'Contenu principal';
      this.updateProgress({ phase: 'encoding', progress: currentProgress, message: `Export ${segmentLabel}...` });

      if (segment.hasTransparency) {
        // Utiliser WebCodecs pour intro/outro
        const blob = await this.exportSegmentWithWebCodecs(
          segment.clips,
          tracks,
          activeSubtitleLanguage,
          subtitleStyle
        );
        videoBlobs.push(blob);
      } else {
        // Utiliser MediaRecorder pour le contenu principal
        const blob = await this.exportSegmentWithMediaRecorder(
          segment.clips,
          tracks,
          activeSubtitleLanguage,
          subtitleStyle
        );
        videoBlobs.push(blob);
      }

      currentProgress += progressPerSegment;
    }

    // 6. Capturer l'audio complet (une seule fois pour toute la timeline)
    this.updateProgress({ phase: 'muxing', progress: 80, message: 'Capture de la piste audio...' });
    audioBlob = await this.captureAudioTrack(clips, tracks);

    // 7. Concatenation des segments vidéo + audio
    this.updateProgress({ phase: 'muxing', progress: 90, message: 'Assemblage des segments...' });
    const finalBlob = await this.concatenateSegments(videoBlobs, audioBlob);

    this.updateProgress({ phase: 'complete', progress: 100, message: 'Export terminé !' });
    return finalBlob;
  }

  /**
   * Segmente le timeline en intro/main/outro basé sur clip.clipRole
   */
  private segmentTimeline(clips: Clip[]): TimelineSegment[] {
    const segments: TimelineSegment[] = [];

    // Trouver intro
    const introClips = clips.filter(c => c.clipRole === 'intro');
    if (introClips.length > 0) {
      const introEnd = Math.max(...introClips.map(c => c.start + c.duration));
      segments.push({
        type: 'intro',
        clips: introClips,
        start: 0,
        end: introEnd,
        hasTransparency: true, // Intro a toujours de la transparence
      });
    }

    // Trouver outro
    const outroClips = clips.filter(c => c.clipRole === 'outro');
    let outroStart = Infinity;
    let outroEnd = 0;
    if (outroClips.length > 0) {
      outroStart = Math.min(...outroClips.map(c => c.start));
      outroEnd = Math.max(...outroClips.map(c => c.start + c.duration));
      segments.push({
        type: 'outro',
        clips: outroClips,
        start: outroStart,
        end: outroEnd,
        hasTransparency: true, // Outro a toujours de la transparence
      });
    }

    // Contenu principal (tout ce qui n'est ni intro ni outro)
    const mainClips = clips.filter(c => !c.clipRole);
    if (mainClips.length > 0) {
      const mainStart = segments.some(s => s.type === 'intro')
        ? segments.find(s => s.type === 'intro')!.end
        : 0;
      const mainEnd = outroStart !== Infinity ? outroStart : Math.max(...mainClips.map(c => c.start + c.duration));

      segments.push({
        type: 'main',
        clips: mainClips,
        start: mainStart,
        end: mainEnd,
        hasTransparency: false, // Pas de transparence dans le contenu principal
      });
    }

    // Trier par ordre chronologique
    return segments.sort((a, b) => a.start - b.start);
  }

  /**
   * Export un segment avec WebCodecs (pour intro/outro)
   */
  private async exportSegmentWithWebCodecs(
    clips: Clip[],
    tracks: Track[],
    activeSubtitleLanguage: string,
    subtitleStyle: SubtitleStyle
  ): Promise<Blob> {
    // Réutiliser le WebCodecsExporter existant
    return this.webCodecsExporter.export(clips, tracks, activeSubtitleLanguage, subtitleStyle);
  }

  /**
   * Export un segment avec MediaRecorder (rapide, pour contenu principal)
   */
  private async exportSegmentWithMediaRecorder(
    clips: Clip[],
    tracks: Track[],
    activeSubtitleLanguage: string,
    subtitleStyle: SubtitleStyle
  ): Promise<Blob> {
    const canvas = new OffscreenCanvas(this.canvasWidth, this.canvasHeight);
    const ctx = canvas.getContext('2d')!;
    // FIX: FrameRenderer constructor expects (width: number, height: number), not (canvas, aspectRatio)
    const renderer = new FrameRenderer(this.canvasWidth, this.canvasHeight);

    // FIX: Preload media before rendering
    await renderer.preloadMedia(clips);

    // Calculer la durée du segment
    const duration = Math.max(...clips.map(c => c.start + c.duration)) - Math.min(...clips.map(c => c.start));
    const totalFrames = Math.ceil(duration * this.config.fps);
    const frameDuration = 1 / this.config.fps;

    // Créer un MediaStream à partir du canvas
    const stream = (canvas as any).captureStream(this.config.fps);

    // Configuration MediaRecorder
    const mimeType = 'video/webm;codecs=vp9';
    if (!MediaRecorder.isTypeSupported(mimeType)) {
      throw new Error('VP9 codec non supporté');
    }

    const mediaRecorder = new MediaRecorder(stream, {
      mimeType,
      videoBitsPerSecond: this.config.videoBitrate,
    });

    const chunks: Blob[] = [];
    mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        chunks.push(event.data);
      }
    };

    // Démarrer l'enregistrement
    mediaRecorder.start();

    // Rendre chaque frame
    const segmentStart = Math.min(...clips.map(c => c.start));
    for (let frame = 0; frame < totalFrames; frame++) {
      const currentTime = segmentStart + (frame * frameDuration);

      // Clear canvas
      ctx.clearRect(0, 0, this.canvasWidth, this.canvasHeight);
      ctx.fillStyle = '#000000';
      ctx.fillRect(0, 0, this.canvasWidth, this.canvasHeight);

      // Rendre la frame
      // FIX: renderFrame expects ctx as first parameter
      await renderer.renderFrame(
        ctx,
        currentTime,
        clips,
        tracks,
        activeSubtitleLanguage,
        subtitleStyle
      );

      // Attendre le prochain frame timing
      await new Promise(resolve => setTimeout(resolve, frameDuration * 1000));
    }

    // Arrêter l'enregistrement
    return new Promise<Blob>((resolve) => {
      mediaRecorder.onstop = () => {
        resolve(new Blob(chunks, { type: mimeType }));
      };
      mediaRecorder.stop();
      stream.getTracks().forEach(track => track.stop());
    });
  }

  /**
   * Export complet avec MediaRecorder (utilisé quand pas d'intro/outro)
   */
  private async exportWithMediaRecorder(
    clips: Clip[],
    tracks: Track[],
    activeSubtitleLanguage: string,
    subtitleStyle: SubtitleStyle
  ): Promise<Blob> {
    const canvas = new OffscreenCanvas(this.canvasWidth, this.canvasHeight);
    const ctx = canvas.getContext('2d')!;
    // FIX: FrameRenderer constructor expects (width: number, height: number), not (canvas, aspectRatio)
    const renderer = new FrameRenderer(this.canvasWidth, this.canvasHeight);

    // FIX: Preload media before rendering
    await renderer.preloadMedia(clips);

    // Calculer la durée totale
    const duration = Math.max(...clips.map(c => c.start + c.duration));
    const totalFrames = Math.ceil(duration * this.config.fps);
    const frameDuration = 1 / this.config.fps;

    // Créer un MediaStream
    const stream = (canvas as any).captureStream(this.config.fps);

    // Ajouter l'audio au stream
    const audioContext = new AudioContext({ sampleRate: this.config.audioSampleRate });
    const audioMixer = new AudioMixer(audioContext.sampleRate);
    const audioClips = clips.filter(c => {
      const track = tracks.find(t => t.id === c.trackId);
      return !track?.isMuted && (c.type === 'video' || c.type === 'audio');
    });

    // Créer une piste audio
    const audioDestination = audioContext.createMediaStreamDestination();
    stream.addTrack(audioDestination.stream.getAudioTracks()[0]);

    // Configuration MediaRecorder avec audio
    const mimeType = 'video/webm;codecs=vp9,opus';
    if (!MediaRecorder.isTypeSupported(mimeType)) {
      throw new Error('VP9+Opus codec non supporté');
    }

    const mediaRecorder = new MediaRecorder(stream, {
      mimeType,
      videoBitsPerSecond: this.config.videoBitrate,
      audioBitsPerSecond: this.config.audioBitrate,
    });

    const chunks: Blob[] = [];
    mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        chunks.push(event.data);
      }
    };

    // Démarrer l'enregistrement
    mediaRecorder.start();

    // Rendre chaque frame
    for (let frame = 0; frame < totalFrames; frame++) {
      const currentTime = frame * frameDuration;
      const progress = 10 + (frame / totalFrames) * 80;
      this.updateProgress({
        phase: 'encoding',
        progress,
        message: `Rendu frame ${frame + 1}/${totalFrames}...`,
        currentFrame: frame + 1,
        totalFrames
      });

      // Clear canvas
      ctx.clearRect(0, 0, this.canvasWidth, this.canvasHeight);
      ctx.fillStyle = '#000000';
      ctx.fillRect(0, 0, this.canvasWidth, this.canvasHeight);

      // Rendre la frame
      // FIX: renderFrame expects ctx as first parameter
      await renderer.renderFrame(
        ctx,
        currentTime,
        clips,
        tracks,
        activeSubtitleLanguage,
        subtitleStyle
      );

      // Mixer et jouer l'audio pour cette frame
      // TODO: Implémenter le mixing audio en temps réel

      // Attendre le prochain frame timing
      await new Promise(resolve => setTimeout(resolve, frameDuration * 1000));
    }

    // Arrêter l'enregistrement
    return new Promise<Blob>((resolve) => {
      mediaRecorder.onstop = () => {
        resolve(new Blob(chunks, { type: mimeType }));
      };
      mediaRecorder.stop();
      stream.getTracks().forEach(track => track.stop());
      audioContext.close();
    });
  }

  /**
   * Capture la piste audio complète avec AudioMixer
   */
  private async captureAudioTrack(clips: Clip[], tracks: Track[]): Promise<Blob> {
    const audioContext = new AudioContext({ sampleRate: this.config.audioSampleRate });
    const audioMixer = new AudioMixer(audioContext.sampleRate);

    const audioClips = clips.filter(c => {
      const track = tracks.find(t => t.id === c.trackId);
      return !track?.isMuted && (c.type === 'video' || c.type === 'audio');
    });

    const duration = Math.max(...clips.map(c => c.start + c.duration));

    // Générer les frames audio
    const audioChunks: Float32Array[] = [];
    const frameDuration = 0.02; // 20ms
    const samplesPerFrame = Math.floor(this.config.audioSampleRate * frameDuration);

    for await (const audioData of audioMixer.generateAudioFrames(audioClips, duration, frameDuration)) {
      // Extraire les données audio
      const buffer = new ArrayBuffer(audioData.numberOfFrames * audioData.numberOfChannels * 4);
      audioData.copyTo(buffer, { planeIndex: 0 });
      audioChunks.push(new Float32Array(buffer));
      audioData.close();
    }

    // Convertir en WAV blob pour simplifier
    const wavBlob = this.createWavBlob(audioChunks, this.config.audioSampleRate);
    await audioContext.close();

    return wavBlob;
  }

  /**
   * Concatene les segments vidéo et ajoute l'audio
   *
   * LIMITATIONS ACTUELLES:
   * - La concatenation simple de blobs WebM ne fonctionne pas toujours correctement
   * - L'audio séparé n'est pas encore multiplexé avec la vidéo
   *
   * TODO FUTUR:
   * - Implémenter avec mp4box.js ou ffmpeg.wasm pour un muxing propre
   * - Supporter le multiplexage audio/vidéo
   * - Gérer les transitions entre segments
   *
   * WORKAROUND ACTUEL:
   * - Si pas d'intro/outro, on utilise WebCodecsExporter directement (fonctionne bien)
   * - Si intro/outro, on tente la concatenation simple (peut ne pas marcher sur tous les navigateurs)
   */
  private async concatenateSegments(videoBlobs: Blob[], audioBlob: Blob | null): Promise<Blob> {
    if (videoBlobs.length === 1 && !audioBlob) {
      return videoBlobs[0];
    }

    // Concatenation simple pour WebM
    // Note: Ceci est une implémentation temporaire qui peut ne pas fonctionner dans tous les cas
    // WebM supporte théoriquement la concatenation directe, mais c'est fragile
    const combinedBlob = new Blob(videoBlobs, { type: 'video/webm' });

    // TODO: Multiplexer l'audio avec la vidéo
    // Pour l'instant, on retourne juste la vidéo (l'audio de chaque segment est déjà inclus)
    return combinedBlob;
  }

  /**
   * Crée un blob WAV à partir de chunks audio
   */
  private createWavBlob(audioChunks: Float32Array[], sampleRate: number): Blob {
    const numChannels = 2;
    const totalSamples = audioChunks.reduce((sum, chunk) => sum + chunk.length, 0) / numChannels;
    const bytesPerSample = 2; // 16-bit PCM
    const blockAlign = numChannels * bytesPerSample;
    const byteRate = sampleRate * blockAlign;
    const dataSize = totalSamples * blockAlign;

    const buffer = new ArrayBuffer(44 + dataSize);
    const view = new DataView(buffer);

    // WAV header
    const writeString = (offset: number, str: string) => {
      for (let i = 0; i < str.length; i++) {
        view.setUint8(offset + i, str.charCodeAt(i));
      }
    };

    writeString(0, 'RIFF');
    view.setUint32(4, 36 + dataSize, true);
    writeString(8, 'WAVE');
    writeString(12, 'fmt ');
    view.setUint32(16, 16, true); // fmt chunk size
    view.setUint16(20, 1, true); // PCM format
    view.setUint16(22, numChannels, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, byteRate, true);
    view.setUint16(32, blockAlign, true);
    view.setUint16(34, bytesPerSample * 8, true);
    writeString(36, 'data');
    view.setUint32(40, dataSize, true);

    // Audio data
    let offset = 44;
    for (const chunk of audioChunks) {
      for (let i = 0; i < chunk.length; i++) {
        const sample = Math.max(-1, Math.min(1, chunk[i]));
        const intSample = sample < 0 ? sample * 0x8000 : sample * 0x7FFF;
        view.setInt16(offset, intSample, true);
        offset += 2;
      }
    }

    return new Blob([buffer], { type: 'audio/wav' });
  }

  private updateProgress(progress: ExportProgress) {
    this.progressCallback(progress);
  }
}
